import { Component, type ErrorInfo, type ReactNode } from "react";

const COPY: Record<string, { title: string; hint: string; retry: string; home: string }> = {
  en: {
    title: "This page could not be displayed",
    hint: "A component failed to load. Reloading usually fixes it.",
    retry: "Reload the page",
    home: "Go to homepage",
  },
  fr: {
    title: "Cette page n'a pas pu s'afficher",
    hint: "Un élément n'a pas pu être chargé. Recharger la page suffit généralement.",
    retry: "Recharger la page",
    home: "Retour à l'accueil",
  },
  es: {
    title: "No se pudo mostrar esta página",
    hint: "Un componente no se pudo cargar. Recargar la página suele solucionarlo.",
    retry: "Recargar la página",
    home: "Ir al inicio",
  },
  zh: {
    title: "此页面无法显示",
    hint: "某个组件加载失败。重新加载页面通常可以解决。",
    retry: "重新加载页面",
    home: "返回首页",
  },
  he: {
    title: "הדף לא ניתן לתצוגה",
    hint: "רכיב לא נטען. בדרך כלל טעינה מחדש פותרת את הבעיה.",
    retry: "טעינה מחדש",
    home: "חזרה לדף הבית",
  },
  ru: {
    title: "Эту страницу не удалось отобразить",
    hint: "Компонент не загрузился. Обычно помогает перезагрузка страницы.",
    retry: "Перезагрузить страницу",
    home: "На главную",
  },
};

type Props = { children: ReactNode };
type State = { error: Error | null };

const currentLang = () => {
  const raw =
    (typeof document !== "undefined" && document.documentElement.lang) || "en";
  const code = raw.split("-")[0];
  return code in COPY ? code : "en";
};

/**
 * Keeps a render-time crash (for example a code-split chunk that could not be
 * fetched) from blanking the whole application: the visitor sees a short
 * message with a reload action instead of an empty screen.
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Render error:", error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const copy = COPY[currentLang()];
    const isRtl = currentLang() === "he";

    return (
      <div
        dir={isRtl ? "rtl" : "ltr"}
        className="min-h-screen bg-background flex flex-col items-center justify-center gap-5 px-6 text-center"
      >
        <div className="max-w-md space-y-3">
          {/* Not a real <h1>: the global heading styles clip a marble texture
              into the text, which makes this message unreadable on a light page. */}
          <div
            role="heading"
            aria-level={1}
            className="font-sans text-2xl md:text-3xl font-semibold text-foreground"
          >
            {copy.title}
          </div>
          <p className="text-foreground/80">{copy.hint}</p>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-lg bg-foreground text-background px-6 py-3 font-medium hover:bg-foreground/90 transition-colors"
          >
            {copy.retry}
          </button>
          <button
            type="button"
            onClick={() => {
              window.location.href = "/";
            }}
            className="rounded-lg border border-border bg-background text-foreground px-6 py-3 font-medium hover:bg-muted transition-colors"
          >
            {copy.home}
          </button>
        </div>
      </div>
    );
  }
}
